import React, { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Box } from "@salestrak/ui";
import { WORKSPACE_NAME_MAX_LENGTH } from "@salestrak/utilities";
import {
  PageHeader,
  PageHeaderSubtitle,
  PageHeaderTitle,
  SETTINGS_CONTENT_MAX_WIDTH,
  SettingsPageGutter,
} from "#app/components/page-header";
import { EditableField } from "#app/components/settings/editable-field";
import { DangerZoneGroup } from "#app/components/settings/danger-zone";
import { WORKSPACE_ANCHORS } from "#app/components/settings-search/rows/workspace";
import { SettingsHighlightTarget } from "#app/components/settings-search/settings-highlight";
import {
  SettingsGroup,
  SettingsGroupRow,
} from "#app/components/settings/settings-group";
import { WorkspaceAvatarPicker } from "#app/components/settings/workspace-avatar-picker";
import { useWorkspaceSettings } from "#app/lib/hooks/use-workspace-settings";
import { SettingsPageSkeleton } from "#app/components/settings/settings-page-skeleton";
import { DeleteWorkspaceDialog } from "#app/components/workspace-deletion/delete-workspace-dialog";

// The API trims and enforces the same rule; checking here keeps a blank or
// over-long name from being sent at all, and says why on blur.
const isValidWorkspaceName = (value: string) => {
  const name = value.trim();
  return name.length > 0 && name.length <= WORKSPACE_NAME_MAX_LENGTH;
};

function WorkspaceSettingsPage() {
  const { workspace, updateName, updateAvatarColor } = useWorkspaceSettings();
  const [deleteOpen, setDeleteOpen] = useState(false);

  // The active workspace resolves during provider boot; this page only
  // renders inside the workspace layout, so a null here is the boot gap,
  // never a terminal state. The header is static, so it renders for real —
  // only the rows are stood in for (delayed, so the boot gap rarely paints).
  if (!workspace) {
    return (
      <Box fill="flex" overflow="auto">
        <PageHeader centered>
          <PageHeaderTitle>Workspace</PageHeaderTitle>
          <PageHeaderSubtitle>
            Manage your workspace's identity and settings.
          </PageHeaderSubtitle>
        </PageHeader>
        <SettingsPageSkeleton groups={[{ rows: 2 }, { rows: 1 }]} />
      </Box>
    );
  }

  return (
    <Box fill="flex" overflow="auto">
      <PageHeader centered>
        <PageHeaderTitle>Workspace</PageHeaderTitle>
        <PageHeaderSubtitle>
          Manage your workspace's identity and settings.
        </PageHeaderSubtitle>
      </PageHeader>
      <Box direction="column" align="center" paddingY="md" paddingX="md">
        <Box direction="column" gap="2xl" fill="width" maxWidth={SETTINGS_CONTENT_MAX_WIDTH}>
          <SettingsGroup label="General">
            <SettingsGroupRow
              highlightAnchor={WORKSPACE_ANCHORS.avatar}
              label="Avatar"
              subtitle="Shown in the workspace switcher"
            >
              <Box direction="row" justify="end">
                <WorkspaceAvatarPicker
                  avatarColor={workspace.avatarColor}
                  logo={workspace.settings?.logo}
                  name={workspace.name}
                  onColorChange={(color) => void updateAvatarColor(color)}
                />
              </Box>
            </SettingsGroupRow>

            <SettingsGroupRow highlightAnchor={WORKSPACE_ANCHORS.name} label="Name">
              <EditableField
                errorMessage={`Enter a name of 1 to ${WORKSPACE_NAME_MAX_LENGTH} characters.`}
                fieldId="name"
                initialValue={workspace.name}
                label="Name"
                onSave={(_fieldId, value) => void updateName(value)}
                validate={isValidWorkspaceName}
              />
            </SettingsGroupRow>
          </SettingsGroup>

          {/* Owner-only, in both directions: the API gates it on the Owner
            * system role, so the button reads the same fact rather than a
            * permission key (Owner is locked-on for every key). */}
          <SettingsHighlightTarget anchor={WORKSPACE_ANCHORS.deleteWorkspace}>
          <DangerZoneGroup
            dangerZone={[
              {
                id: "delete-workspace",
                label: "Delete Workspace forever",
                // The row states the intent; the dialog carries the terms
                // (the 30-day clock, the lock, the Owner undo).
                subtitle: "Schedule this Workspace to be permanently deleted",
                buttonLabel: "Delete Workspace",
                variant: "destructive",
                ...(workspace.viewerIsOwner
                  ? { onClick: () => setDeleteOpen(true) }
                  : { disabledReason: "Only a workspace Owner can delete the workspace" }),
              },
            ]}
          />
          </SettingsHighlightTarget>
          {deleteOpen && (
            <DeleteWorkspaceDialog
              onOpenChange={(open) => {
                if (!open) setDeleteOpen(false);
              }}
              workspace={workspace}
            />
          )}

          <SettingsPageGutter />
        </Box>
      </Box>
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/settings/workspace")({
  component: WorkspaceSettingsPage,
});
